import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useWorkflowNudge from 'hooks/useWorkflowNudge';
import Workflow from 'models/Workflow';
import { WorkflowAction } from 'models/WorkflowAction';
import { attrValuesQueryKeys } from 'services/api/attrValue';
import {
  amendWorkflowAction,
  archiveWorkflow,
  completeWorkflowAction,
  completeWorkflowActions,
  dispatchWorkflowAction,
  invokeWorkflowAction,
  markWorkflowActionViewed,
  resendWorkflowAction,
  saveWorkflowActionProgress,
  showWorkflow,
  updateWorkflow,
  UpdateWorkflowInput,
  WorkflowActionIntent,
  workflowQueryKeys,
  WorkflowValuesInput,
} from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';

// A write's response (create / value-write / complete / rerun / dismiss) now
// reflects the FINAL settled state — the evaluator runs synchronously, including
// re-run cascades (P1-1) — so the acting user's own change renders straight from
// the response.

// The fallback cadence only (OLIO-2602): the WorkflowChannel nudge is the primary
// freshness signal; this slow poll covers dropped sockets and missed events.
const FALLBACK_POLL_INTERVAL_MS = 60_000;

/**
 * The single data hook for a workflow run (the key WEB-0 architectural ask).
 *
 * All fetching lives here so the rest of the UI is agnostic to *how* fresh data
 * arrives. Today it polls `GET /api/workflows/:id` until the run is terminal;
 * swapping to a WebSocket subscription in Phase 2 is a one-file change — only the
 * `useQuery` block below changes, the returned shape stays the same.
 */
export default function useWorkflow(id: string | undefined) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const query = useQuery({
    queryKey: workflowQueryKeys.show(id ?? ''),
    queryFn: ({ signal }) => showWorkflow(id!, signal),
    enabled: !!id,
    // --- swap point: replace this background polling with a subscription in Phase 2 ---
    // Background-only: the acting user's writes already return settled state; this
    // catches other users' changes while the run is live, stopping once terminal.
    refetchInterval: (q) => (q.state.data && q.state.data.isTerminal ? false : FALLBACK_POLL_INTERVAL_MS),
    refetchIntervalInBackground: true,
  });

  // Live updates (OLIO-2602): the API broadcasts an id-only nudge whenever this run
  // settles — the shared wiring debounces the burst and invalidates this query, so
  // the fetch stays the single source of truth.
  useWorkflowNudge(id, workflowQueryKeys.show(id ?? ''));

  const onWriteSuccess = (workflow: Workflow) => {
    if (id) queryClient.setQueryData(workflowQueryKeys.show(id), workflow);
    // The list prefix too, not just the show key. A write settles the run, which can move it
    // out of a filtered projection entirely — the Tasks list is `status = action_required`, so
    // the card a person just resolved has to vanish on their own write rather than linger until
    // the broadcast round-trip or the fallback poll.
    queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists });
    // Answering Confirm Referral Source CREATES an Attr Value (OLIO-3158), so every picker over
    // that list is now stale. Nothing invalidated it, and the list is fetched once and cached for
    // the session — so a source added on one referral did not appear on another until the page was
    // reloaded and the cache went with it (QA, Sep 2).
    //
    // Invalidated on every workflow write rather than only that one, because this hook cannot tell
    // which completion it just ran. That is deliberately the cheap direction: the query is
    // `enabled: false`, so marking it stale refetches nothing now — the next picker to open pays
    // one request. Missing the invalidation is a wrong list; making it twice is a round trip.
    queryClient.invalidateQueries({ queryKey: [attrValuesQueryKeys.index] });
  };

  const onWriteError = () => addToast({ text: 'Something went wrong. Please try again' });

  // Sending to an EHR gets its own error handler, because the generic one is actively wrong here.
  // "Something went wrong. Please try again" is bad advice for the refusal the API now returns (409):
  // the request was fine, the row had simply moved on — the step is already sending, possibly from
  // this coordinator's other tab — and trying again just repeats it. The server's own sentence says
  // what to do, so show that.
  //
  // The refetch is the substantive half. A 409 means this client's view of the action is stale by
  // definition, so pulling the truth back corrects the card without anyone reloading the page.
  const onSendError = (error: unknown) => {
    const response = (error as { response?: { status?: number; data?: { errors?: Record<string, string[]> } } })
      ?.response;
    const message = response?.data?.errors && Object.values(response.data.errors).flat()[0];

    addToast({ text: message ?? 'Something went wrong. Please try again' });
    if (response?.status === 409 && id) queryClient.invalidateQueries({ queryKey: workflowQueryKeys.show(id) });
  };

  const actionMutation = useMutation({
    mutationFn: ({ actionId, outputs }: { actionId: string; outputs: WorkflowValuesInput }) =>
      completeWorkflowAction({ workflowId: id!, actionId, outputs }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  // Bulk completion over a selection (Facility Decision Accept All / Deny All). One request, so
  // the settled snapshot lands once — there is no ordering to defend against, and the domain
  // records the decision as a single moment rather than N completions to reassemble.
  const actionsMutation = useMutation({
    mutationFn: ({ actionIds, outputs }: { actionIds: string[]; outputs: WorkflowValuesInput }) =>
      completeWorkflowActions({ workflowId: id!, actionIds, outputs }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  // Amend uses the send-style error handler, not the generic one (OLIO-2973): changing a decided
  // referral's outcome can be REFUSED with a sentence worth reading — "a newer referral already
  // exists, make the change there" (409, superseded by a resend) or "only the recorded outcome can
  // be changed" (422). "Something went wrong. Please try again" is actively wrong for both: the
  // request was fine, the state refused it, and trying again changes nothing. The 409 refetch also
  // pulls the successor-aware state this client clearly doesn't have.
  const amendMutation = useMutation({
    mutationFn: ({ actionId, outputs }: { actionId: string; outputs: WorkflowValuesInput }) =>
      amendWorkflowAction({ workflowId: id!, actionId, outputs }),
    onSuccess: onWriteSuccess,
    onError: onSendError,
  });

  // Stages the outputs on the action without completing it (save_progress) — the
  // hand-off flow parks retrieved values here for a person to confirm. `narrationHint` is
  // optional; SexOffenderCheck's Dismiss-All-in-bucket flow uses it to key the activity-log
  // entry off the bucket name rather than the diff-based per-card phrasing.
  const saveProgressMutation = useMutation({
    mutationFn: ({
      actionId,
      outputs,
      narrationHint,
    }: {
      actionId: string;
      outputs: WorkflowValuesInput;
      narrationHint?: string;
    }) => saveWorkflowActionProgress({ workflowId: id!, actionId, outputs, narrationHint }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  // Persist corrected demographics on the referral (OLIO-2846). The sync modal writes these before
  // dispatching so the push reads the corrected data — and so a Retry, which re-reads the values, keeps
  // reading it. Works on a terminal (Selected) referral: the API's write_values is not terminal-gated.
  const updateValuesMutation = useMutation({
    mutationFn: (values: WorkflowValuesInput) => updateWorkflow({ workflowId: id!, values }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  // The by-property slideout's commit (OLIO-3022): one request carrying every action the
  // person's single gesture completes. Deliberately NOT a loop over completeAction — the
  // gesture is atomic on the API, and a client-side sequence would let a run come to rest
  // half-answered when the second call failed.
  const submitMutation = useMutation({
    mutationFn: ({ actionCompletions, values }: Pick<UpdateWorkflowInput, 'actionCompletions' | 'values'>) =>
      updateWorkflow({ workflowId: id!, actionCompletions, values }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  // Start a user-dispatched action's work (OLIO-2846). Its own mutation rather than a variant of
  // invokeMutation because it carries `outputs`. FIRST send only — see resendMutation for a second
  // attempt, which the API requires to be a new instance.
  const dispatchMutation = useMutation({
    mutationFn: ({ actionId, outputs }: { actionId: string; outputs?: WorkflowValuesInput }) =>
      dispatchWorkflowAction({ workflowId: id!, actionId, outputs }),
    onSuccess: onWriteSuccess,
    onError: onSendError,
  });

  // Send again after an attempt that already finished — rerun, then dispatch the fresh instance
  // (design 010). Deliberately NOT folded into dispatchMutation: the two are one gesture to a
  // coordinator but two writes to the engine, and the intermediate workflow from the rerun must not
  // reach the cache. Writing it would render a card mid-flight — a pending instance with no result,
  // i.e. "Ready to send" — between the two calls. Only the final dispatch's workflow is committed.
  const resendMutation = useMutation({
    mutationFn: ({ action, outputs }: { action: WorkflowAction; outputs?: WorkflowValuesInput }) =>
      resendWorkflowAction({ workflowId: id!, action, outputs }),
    onSuccess: onWriteSuccess,
    onError: onSendError,
  });

  // Retry a failed automated step: restarting the errored action re-runs it through
  // dispatch, so a headless replay re-dispatches with the saved recording and an
  // extension run re-arms the launch pane. Same consent mechanism the referral page's
  // correction cascade uses (restart_action_ids).
  const restartMutation = useMutation({
    mutationFn: ({ actionIds }: { actionIds: string[] }) =>
      updateWorkflow({ workflowId: id!, restartActionIds: actionIds }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  const invokeMutation = useMutation({
    mutationFn: ({ actionId, invokeAction }: { actionId: string; invokeAction: WorkflowActionIntent }) =>
      invokeWorkflowAction({ workflowId: id!, actionId, invokeAction }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  // Viewing an action records the view AND completes it (action_required → completed). Unlike the
  // other writes, the endpoint returns ONLY the view attribution — not the settled workflow — so
  // we must NOT write the response into the cache (a partial payload would blank the page). Instead
  // we refetch to pick up the stamp + settle authoritatively. Silent on error — a failed view-stamp
  // is a side effect of opening the drawer and shouldn't interrupt reviewing.
  // Archiving ends the referral. Its response is the settled, canceled run, so the same
  // onWriteSuccess that every other write uses leaves the page reading correctly without a
  // refetch — and the query's poll stops on its own, since it halts once terminal.
  const archiveMutation = useMutation({
    mutationFn: () => archiveWorkflow(id!),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  const markViewedMutation = useMutation({
    mutationFn: ({ actionId }: { actionId: string }) => markWorkflowActionViewed({ workflowId: id!, actionId }),
    onSuccess: () => {
      if (id) queryClient.invalidateQueries({ queryKey: workflowQueryKeys.show(id) });
    },
  });

  return {
    workflow: query.data,
    isLoading: query.isPending && !!id,
    isError: query.isError,
    // True while a poll or write is in flight — drives a subtle "updating" affordance.
    isFetching: query.isFetching,
    refetch: query.refetch,
    completeAction: actionMutation.mutate,
    // The collection route: one request for a whole selection, and for a single row too.
    completeActions: actionsMutation.mutate,
    completeActionsAsync: actionsMutation.mutateAsync,
    isCompletingActions: actionsMutation.isPending,
    // Awaitable variant — callers that need to know the write settled before they
    // close a drawer or clear their own state.
    completeActionAsync: actionMutation.mutateAsync,
    isCompletingAction: actionMutation.isPending,
    amendAction: amendMutation.mutate,
    amendActionAsync: amendMutation.mutateAsync,
    isAmendingAction: amendMutation.isPending,
    saveActionProgress: saveProgressMutation.mutate,
    isSavingActionProgress: saveProgressMutation.isPending,
    submitActionCompletions: submitMutation.mutate,
    isSubmittingCompletions: submitMutation.isPending,
    restartActions: restartMutation.mutate,
    isRestartingActions: restartMutation.isPending,
    invokeAction: invokeMutation.mutate,
    isInvokingAction: invokeMutation.isPending,
    updateValuesAsync: updateValuesMutation.mutateAsync,
    dispatchAction: dispatchMutation.mutate,
    resendAction: resendMutation.mutate,
    resendActionAsync: resendMutation.mutateAsync,
    // Awaitable so the modal can close only once the dispatch has actually been accepted — a modal
    // that closes optimistically and then 422s leaves the coordinator with no idea what happened.
    dispatchActionAsync: dispatchMutation.mutateAsync,
    // One flag for both: a coordinator is "sending" whether this is the first attempt or a retry, and
    // the button they are looking at is the same button.
    isDispatchingAction: dispatchMutation.isPending || resendMutation.isPending,
    markActionViewed: markViewedMutation.mutate,
    archiveWorkflow: archiveMutation.mutate,
    isArchivingWorkflow: archiveMutation.isPending,
  };
}
